// src/components/GuestCheckIn.js (FINAL COMPACT & ORGANIZED VERSION)
import React, { useState, useEffect } from "react";
import "../index.css"; // 🔥 FIXED: Added the CSS import here
import { db } from "../firebase";
import { useTranslation } from 'react-i18next';

import {
  collection,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

import SmartAssignmentLogic from "./SmartAssignmentLogic";
import GuestRegistrationForm from "../components/GuestRegistrationForm";
import GuestStatusChecker from "./GuestStatusChecker"; 
import AIGuestAssistant from "../components/AIGuestAssistant";

// === CLOUDINARY CONFIGURATION ===
const CLOUD_NAME = "dift6go5s"; 
const UPLOAD_PRESET = "chat_preset"; 
// ==================================

const calculateTotalDays = (checkIn, checkOut) => {
  const oneDay = 24 * 60 * 60 * 1000;
  const first = new Date(checkIn);
  const second = new Date(checkOut);
  if (isNaN(first) || isNaN(second) || second <= first) return 0;
  return Math.ceil((second - first) / oneDay);
};

const generateAccessKey = () =>
  Math.random().toString(36).substring(2, 8).toUpperCase();

const initialGuestState = {
  fullName: "",
  email: "",
  phone: "",
  country: "",
  hotelId: "",
  roomId: "",
  roomNumber: "",
  roomPrice: 0,
  checkInDate: new Date().toISOString().split("T")[0],
  checkOutDate: "",
  paymentMethod: "Pay at Hotel",
  extras: "",
  status: "CHECKED_IN",
  keyRetrievalMethod: "",
  invoiceUrl: "",
  basePrice: 0.00,
  upsellsTotal: 0.00,
};

export default function GuestCheckIn() {
  const { user } = useAuth() || {};
  const [allHotels, setAllHotels] = useState([]);
  const [hotelSearchTerm, setHotelSearchTerm] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState("");
  const [guest, setGuest] = useState(initialGuestState);
  const { t } = useTranslation();

  const [activeSection, setActiveSection] = useState("digital");

  const totalDays = calculateTotalDays(guest.checkInDate, guest.checkOutDate);
  const totalPrice = totalDays * guest.roomPrice;

  const resetForm = () => {
    setGuest(initialGuestState);
    setIdFile(null);
    setHotelSearchTerm("");
    setAvailableRooms([]);
    setSuccessMessage(null);
    setError("");
  };

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const snap = await getDocs(collection(db, "hotels"));
        setAllHotels(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setDataLoaded(true);
      } catch (err) {
        console.error("Error fetching hotels:", err);
      }
    };
    fetchHotels();
  }, []);

  const fetchRooms = async (hotelId) => {
    if (!hotelId) return setAvailableRooms([]);
    setLoading(true);
    try {
      const q = query(
        collection(db, "rooms"),
        where("hotelId", "==", hotelId),
        where("status", "==", "available")
      );
      const snap = await getDocs(q);
      setAvailableRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching rooms:", err);
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === "search" && dataLoaded && guest.hotelId) {
      fetchRooms(guest.hotelId);
    }
  }, [guest.hotelId, dataLoaded, activeSection]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let update = { [name]: value };

    if (name === "hotelId") {
      update = { ...update, roomId: "", roomNumber: "", roomPrice: 0, basePrice: 0, upsellsTotal: 0 };
    }

    if (name === "roomId") {
      const selected = availableRooms.find((r) => r.id === value);
      const price = selected ? selected.price : 0;
      update.roomNumber = selected ? selected.roomNumber : "";
      update.roomPrice = price;
      update.basePrice = 0.00; 
      update.upsellsTotal = 0.00;
    }

    setGuest((prev) => ({ ...prev, ...update }));
    setError("");
    setSuccessMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage(null);

    if (!guest.hotelId || !guest.roomId || !guest.fullName || totalDays <= 0 || !idFile) {
      return setError("⚠️ Please complete all required fields and upload ID/Passport.");
    }

    setLoading(true);
    let idUrl = "";
    let submissionSuccess = false; 

    try {
      const formData = new FormData();
      formData.append('file', idFile);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', `guest_ids/${guest.hotelId}`); 

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );

      const data = await cloudinaryResponse.json();
      if (!cloudinaryResponse.ok || data.error) throw new Error('Cloudinary upload failed.');
      
      idUrl = data.secure_url;

      const accessKey = generateAccessKey();
      const hotelDoc = await getDoc(doc(db, "hotels", guest.hotelId));
      const hotelName = hotelDoc.exists() ? hotelDoc.data().name : "Unknown Hotel";
      const checkedInByValue = (user && user.email) ? user.email : "Public/Guest";

      const guestData = {
        ...guest,
        basePrice: totalPrice,
        upsellsTotal: 0.00,
        bookingSource: 'Staff',
        totalPrice, 
        totalDays,
        hotelName,
        idUrl,
        checkInTimestamp: new Date().toISOString(),
        checkedInBy: checkedInByValue,
        accessKey,
        status: "CHECKED_IN",
        keyRetrievalMethod: guest.keyRetrievalMethod || "Front Desk",
        invoiceUrl: guest.invoiceUrl || "N/A",
      };

      const guestDoc = await addDoc(collection(db, "guests"), guestData);

      await updateDoc(doc(db, "rooms", guest.roomId), {
        status: "occupied",
        currentGuestId: guestDoc.id,
      });

      setSuccessMessage({
        name: guest.fullName,
        room: guest.roomNumber,
        totalPrice,
        payment: guest.paymentMethod,
        checkOutDate: guest.checkOutDate,
        accessKey,
        keyRetrievalMethod: guestData.keyRetrievalMethod,
      });
      
      submissionSuccess = true;

    } catch (apiError) {
      console.error("Check-in failed:", apiError);
      setError(`❌ Check-in failed: ${apiError.message}.`);
    } finally {
      setLoading(false);
      if (submissionSuccess) {
          setTimeout(() => resetForm(), 10000);
      }
    }
  };

  const filteredHotels = allHotels.filter((h) =>
    (h?.name || "").toLowerCase().includes(hotelSearchTerm.toLowerCase())
  );

  return (
    <>
    <div className="container" style={{ paddingTop: "0", maxWidth: "900px", margin: "30px auto" }}>
      <SmartAssignmentLogic />

      {/* --- GRID BASED BUTTON NAVIGATION --- */}
      <div className="navigation-links">
        <button
          onClick={() => setActiveSection("digital")}
          className="btn-nav"
          style={{
            backgroundColor: activeSection === "digital" ? "#007bff" : "#ffffff",
            color: activeSection === "digital" ? "white" : "#333",
            border: activeSection === "digital" ? "none" : "1px solid #ddd"
          }}
        >
          📝 {t('registration.title')}
        </button>

        <button
          onClick={() => setActiveSection("search")}
          className="btn-nav"
          style={{
            backgroundColor: activeSection === "search" ? "#007bff" : "#ffffff",
            color: activeSection === "search" ? "white" : "#333",
            border: activeSection === "search" ? "none" : "1px solid #ddd"
          }}
        >
          🔑 {t('checkin.title')}
        </button>

        <button
          onClick={() => setActiveSection("status")}
          className="btn-nav"
          style={{
            backgroundColor: activeSection === "status" ? "#17a2b8" : "#ffffff",
            color: activeSection === "status" ? "white" : "#333",
            border: activeSection === "status" ? "none" : "1px solid #ddd"
          }}
        >
          🔍 {t('checker.check_btn')}
        </button>
      </div>

      {activeSection === "digital" && (
        <GuestRegistrationForm
          allHotels={allHotels}
          onComplete={() => setActiveSection("search")}
        />
      )}
      
      {activeSection === "search" && (
        <div className="card">
          <h2>{t('checkin.title')}</h2>

          {successMessage && (
            <div className="success-message info-box">
              {t('registration.booking_details_submitted')} <strong>{successMessage.name}</strong>!
              <div>{t('checker.assigned_room')} <strong>{successMessage.room}</strong></div>
              <div>{t('registration.subtotal')}: <strong>${successMessage.totalPrice.toFixed(2)}</strong> ({successMessage.payment})</div>
              <div>{t('registration.checkout_label')} {successMessage.checkOutDate}</div>
              <h3 style={{ marginTop: "15px", color: "#28a745" }}>
                {t('checker.digital_key_label')} <strong>{successMessage.accessKey}</strong>
              </h3>
            </div>
          )}

          {error && <p className="error-message info-box">{error}</p>}

          {!successMessage && (
            <form onSubmit={handleSubmit}>
              <h3>{t('registration.section1_title')}</h3>
              <input name="fullName" placeholder={t('registration.full_name_label')} value={guest.fullName} onChange={handleChange} required />
              <input name="email" type="email" placeholder={t('registration.email_label')} value={guest.email} onChange={handleChange} required />
              <input name="phone" placeholder={t('registration.phone_label')} value={guest.phone} onChange={handleChange} />
              
              <h3>{t('registration.step1')}</h3>
              <select name="hotelId" value={guest.hotelId} onChange={handleChange} required>
                <option value="">{t('registration.select_hotel_option')}</option>
                {filteredHotels.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>

              <select name="roomId" value={guest.roomId} onChange={handleChange} required disabled={!guest.hotelId || loading}>
                <option value="">{t('registration.select_room_option')}</option>
                {availableRooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {t('checker.room_prefix')} {r.roomNumber} - ${r.price}/{t('registration.night')}
                  </option>
                ))}
              </select>

              <div className="flex-row">
                <input type="date" name="checkInDate" value={guest.checkInDate} onChange={handleChange} required />
                <input type="date" name="checkOutDate" value={guest.checkOutDate} onChange={handleChange} required />
              </div>

              <select name="keyRetrievalMethod" value={guest.keyRetrievalMethod} onChange={handleChange} required>
                <option value="">{t('checker.room_access_method_label')}</option>
                <option value="Front Desk">{t('checker.manual_key')}</option>
                <option value="Key Card Dispenser">{t('checker.digital_key')}</option>
              </select>

              <label>{t('registration.upload_id_label')}:</label>
              <input type="file" onChange={(e) => setIdFile(e.target.files[0])} required />

              {totalDays > 0 && guest.roomPrice > 0 && (
                <div className="total-price" style={{ textAlign: 'center' }}>
                  {t('registration.total_estimated_bill')}: ${totalPrice.toFixed(2)}
                </div>
              )}

              <button type="submit" disabled={loading} style={{ marginTop: "20px" }}>
                {loading ? t('checkin.processing') : t('checkin.finalize')}
              </button>
            </form>
          )}
        </div>
      )}
      
      {activeSection === "status" && (
        <GuestStatusChecker />
      )}
    </div>
    <AIGuestAssistant />
    </>
  );
}